import React, { useEffect } from 'react';
import { X, Clock, FileText, Trash2 } from 'lucide-react';

export type EventoQuickViewProps = Readonly<{
  evento: any;
  onClose: () => void;
  onDelete: (id: string) => void;
  onEdit?: (evento: any) => void;
}>;

const formatHour12 = (timeStr: string) => {
  if (!timeStr) return '';
  const [h, m] = timeStr.split(':');
  const d = new Date();
  d.setHours(Number.parseInt(h, 10), Number.parseInt(m, 10));
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });
};

export function EventoQuickViewSheet({ evento, onClose, onDelete, onEdit }: EventoQuickViewProps) {


  useEffect(() => {
    if (evento) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [evento]);

  if (!evento) return null;

  const isDigital = evento.tipo === 'cita_digital';
  const isManual = evento.tipo === 'cita_manual';

  let eventColor = '#CBD5E1'; // slate
  if (isDigital) eventColor = '#3B82F6';
  else if (isManual) eventColor = '#10B981';

  let statusBg = 'rgba(241, 245, 249, 0.8)';
  let statusTextColor = '#475569';
  let estadoText = evento.estado ? evento.estado.replaceAll('_', ' ').toUpperCase() : 'BLOQUEADO';

  let tipoBadgeText = 'BLOQUEO';
  if (isDigital) tipoBadgeText = 'APP PACIENTE';
  else if (isManual) tipoBadgeText = 'PRESENCIAL';

  if (evento.estado === 'pendiente_verificacion_pago') {
    statusBg = 'rgba(254, 243, 199, 0.8)';
    statusTextColor = '#D97706';
  } else if (evento.estado === 'confirmada') {
    statusBg = 'rgba(209, 250, 229, 0.8)';
    statusTextColor = '#059669';
  } else if (evento.estado === 'completada' || evento.estado === 'atendida') {
    statusBg = 'rgba(219, 234, 254, 0.8)';
    statusTextColor = '#1D4ED8';
  } else if (evento.estado === 'no_show_paciente') {
    statusBg = 'rgba(254, 226, 226, 0.8)';
    statusTextColor = '#DC2626';
  }

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
    <div style={{
      position: 'fixed', inset: 0, backgroundColor: 'rgba(15, 23, 42, 0.4)', backdropFilter: 'blur(4px)',
      zIndex: 100, display: 'flex', alignItems: 'flex-end', justifyContent: 'center'
    }} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{
        backgroundColor: '#FFFFFF', width: '100%', maxWidth: '500px',
        borderRadius: '24px 24px 0 0', padding: 0,
        maxHeight: '90vh', display: 'flex', flexDirection: 'column',
        boxShadow: '0 -10px 40px rgba(0,0,0,0.1)'
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', maxHeight: '90vh', overflow: 'hidden' }}>
        {/* Encabezado Visual */}
        <div style={{ 
          backgroundColor: eventColor, 
          padding: '24px 20px', 
          color: '#FFF', 
          position: 'relative',
          borderRadius: '24px 24px 0 0',
        }}>
          <button 
            type="button"
            onClick={onClose}
            style={{ 
              position: 'absolute', top: '16px', right: '16px', 
              background: 'rgba(255,255,255,0.2)', border: 'none', 
              borderRadius: '50%', width: '32px', height: '32px', 
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', color: '#FFF'
            }}
          >
            <X size={18} />
          </button>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <span style={{ 
              backgroundColor: 'rgba(255,255,255,0.2)', padding: '4px 8px', 
              borderRadius: '6px', fontSize: '11px', fontWeight: 600, letterSpacing: '0.5px' 
            }}>
              {tipoBadgeText}
            </span>
            {evento.estado && (
              <span style={{ 
                backgroundColor: statusBg, color: statusTextColor, padding: '4px 8px', 
                borderRadius: '6px', fontSize: '11px', fontWeight: 700, letterSpacing: '0.5px' 
              }}>
                {estadoText}
              </span>
            )}
          </div>
          
          <h2 style={{ margin: '0 0 4px 0', fontSize: '22px', fontWeight: 800 }}>
            {evento.titulo}
          </h2>
        </div>

        {/* Detalles */}
        <div style={{ padding: '24px 20px', display: 'flex', flexDirection: 'column', gap: '16px', overflowY: 'auto' }}>
          
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
            <Clock size={20} color="#64748B" style={{ marginTop: '2px' }} />
            <div>
              <div style={{ fontSize: '13px', color: '#64748B', fontWeight: 600 }}>Horario</div>
              <div style={{ fontSize: '16px', color: '#0F172A', fontWeight: 500 }}>
                {formatHour12(evento.inicio)} — {formatHour12(evento.fin)}
              </div>
            </div>
          </div>

          {evento.subtitulo && (
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
              <FileText size={20} color="#64748B" style={{ marginTop: '2px' }} />
              <div>
                <div style={{ fontSize: '13px', color: '#64748B', fontWeight: 600 }}>Nota / Motivo</div>
                <div style={{ fontSize: '15px', color: '#0F172A' }}>{evento.subtitulo}</div>
              </div>
            </div>
          )}
          
        </div>

        <div style={{ padding: '16px 20px', borderTop: '1px solid #F1F5F9', display: 'flex', gap: '12px' }}>
          {/* Botón de Editar oculto temporalmente (funcionalidad en desarrollo)
          <button 
            onClick={() => onEdit?.(evento)}
            style={{ 
              flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
              padding: '12px', backgroundColor: '#F8FAFC', border: '1px solid #E2E8F0', 
              borderRadius: '12px', color: '#475569', fontWeight: 600, fontSize: '15px',
              cursor: 'pointer'
            }}
          >
            <Edit2 size={18} />
            Editar
          </button>
          */}

          <button 
            type="button"
            onClick={() => onDelete(evento.id)}
            style={{ 
              flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
              padding: '12px', backgroundColor: '#FEF2F2', border: '1px solid #FECACA', 
              borderRadius: '12px', color: '#DC2626', fontWeight: 600, fontSize: '15px',
              cursor: 'pointer'
            }}
          >
            <Trash2 size={18} />
            Cancelar Cita
          </button>
        </div>

        </div>

      </div>
    </div>
  );
}
